import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Volume2 } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import EmptyState from '../../components/EmptyState';

const AdminContentTones = () => {
  const [tones, setTones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTone, setSelectedTone] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', isActive: true });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTones();
  }, []);

  const fetchTones = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/content/tones');
      if (response.data.success) {
        // Transform is_active (0/1) to isActive (boolean)
        const transformedTones = (response.data.tones || []).map(tone => ({
          ...tone,
          isActive: tone.is_active === 1 || tone.is_active === true || tone.isActive === true
        }));
        setTones(transformedTones);
      }
    } catch (error) {
      toast.error('Failed to load tones');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setSelectedTone(null);
    setFormData({ name: '', description: '', isActive: true });
    setShowModal(true);
  };

  const openEditModal = (tone) => {
    setSelectedTone(tone);
    const isActive = tone.is_active === 1 || tone.is_active === true || tone.isActive === true;
    setFormData({
      name: tone.name || '',
      description: tone.description || '',
      isActive: isActive,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Tone name is required');
      return;
    }

    try {
      setSaving(true);
      const url = selectedTone 
        ? `/admin/content/tones/${selectedTone.id}`
        : '/admin/content/tones';
      const method = selectedTone ? 'put' : 'post';
      
      const response = await api[method](url, formData);
      if (response.data.success) {
        toast.success(selectedTone ? 'Tone updated successfully' : 'Tone created successfully');
        setShowModal(false);
        fetchTones();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save tone');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (tone) => {
    setSelectedTone(tone);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await api.delete(`/admin/content/tones/${selectedTone.id}`);
      if (response.data.success) {
        toast.success('Tone deleted successfully');
        setShowDeleteModal(false);
        fetchTones();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete tone');
    } finally {
      setDeleting(false);
    }
  };

  const filteredTones = tones.filter(tone =>
    tone.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminSidebar>
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">
            Story Tones
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Manage story tones for personalized stories</p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
        >
          <Plus className="h-5 w-5" />
          Add Tone
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tones..."
            className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base transition-all"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : filteredTones.length === 0 ? (
        <EmptyState
          icon={<Volume2 className="h-7 w-7" />}
          title={search ? `No tones found for "${search}"` : 'No tones yet'}
          description={search ? 'Try adjusting your search terms' : 'Create your first story tone to get started'}
          action={search ? null : { label: 'Add Tone', onClick: openAddModal }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTones.map((tone) => (
            <div
              key={tone.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{tone.name}</h3>
                  {tone.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{tone.description}</p>
                  )}
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    tone.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {tone.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditModal(tone)}
                  className="flex-1 px-3 py-2 text-sm font-medium text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                >
                  <Edit className="h-4 w-4 inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => openDeleteConfirm(tone)}
                  className="px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <Modal
          title={selectedTone ? 'Edit Tone' : 'Add Tone'}
          onClose={() => setShowModal(false)}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tone Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                placeholder="e.g., Entertaining, Educational, Inspirational"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                rows="3"
                placeholder="Tone description..."
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Active Status</span>
              <button
                onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  formData.isActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {formData.isActive ? 'Active' : 'Inactive'}
              </button>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : selectedTone ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && selectedTone && (
        <ConfirmDialog
          title="Delete Tone"
          description={`Are you sure you want to delete "${selectedTone.name}"? This action cannot be undone.`}
          confirmLabel={deleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={handleDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedTone(null);
          }}
        />
      )}
    </AdminSidebar>
  );
};

export default AdminContentTones;

